import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { onAuthStateChanged } from "firebase/auth";
import { ref as storageObjectRef, uploadBytes } from "firebase/storage";
import {
  addDays,
  addProjectWorkingDays,
  addWorkingDays,
  todayLocalDate,
  workingDaysBetween,
} from "@/utils/dates";
import { api, ApiError } from "@/services/api";
import {
  auth,
  completeExternalMfaSignIn,
  externalMfaSessionStatus,
  signInDemo,
  signInExternal,
  signInMicrosoft,
  signOutUser,
  storage,
  type ExternalMfaChallenge,
  useEmulators,
} from "@/services/firebase";
import {
  activityDto,
  commentDto,
  fileDto,
  fromStatus,
  fromVisibility,
  notificationDto,
  myWorkDto,
  participantDto,
  projectDto,
  riskDto,
  sectionDto,
  sessionDto,
  taskDto,
  taskPatchDto,
} from "@/services/dto";
import type { ProjectionCollection, RealtimeState } from "@/services/realtime";
import {
  demoBundle,
  demoUsers,
  notifications as demoNotifications,
  projects as demoProjects,
} from "@/data/demo";
import type {
  NotificationPreferences,
  NotificationItem,
  DirectoryPerson,
  DirectoryPeoplePage,
  DirectorySyncStatus,
  EmailTemplatePreview,
  ExportJob,
  Organization,
  Participant,
  ProjectAccess,
  BulkTaskEditChanges,
  BulkTaskEditPreview,
  ProjectBundle,
  ProjectBrief,
  ProjectInvitation,
  ProjectRole,
  ProjectScheduleMovePreview,
  MyWorkItem,
  ProjectSummary,
  ProjectTemplate,
  ProjectTask,
  RichTextDocument,
  SearchResult,
  ScheduleImpact,
  SessionUser,
  TaskComment,
  TaskStatus,
  TaskImportPreview,
  Visibility,
  WorkspaceSettings,
} from "@/types/models";
import { microsoftWorkspaceErrorMessage } from "@/utils/auth-errors";
import {
  calculatedWorkItemProgress,
  contentDrivenStatus,
} from "@/utils/work-items";
import {
  emptyRichTextDocument,
  richTextFromLegacyProjectBrief,
  richTextMentionIds,
  richTextPlainText,
} from "@/utils/rich-text";
import { DEFAULT_VISIBLE_STATUSES } from "@/utils/task-filters";
import { projectRoleDefinition } from "@/utils/project-roles";
import { fileUploadErrorMessage } from "@/utils/file-upload-errors";
import {
  taskDeletionBlockedMessage,
  taskDeletionBlockers,
} from "@/utils/task-deletion";

function violationMessage(violation: { code: string }) {
  const messages: Record<string, string> = {
    schedule_date_not_working:
      "Choose a weekday that is available in the project calendar.",
    completed_item_would_move: "Completed work cannot be moved.",
    completed_task_dependency_conflict:
      "This change would move completed work. Adjust the dependency or choose different dates.",
    project_start_boundary_crossed:
      "Choose dates on or after the project start date.",
    project_boundary_extension_required:
      "A project owner must extend the target date before applying this change.",
    project_boundary_reason_required:
      "Add a reason for extending the project target before applying this change.",
    schedule_version_conflict:
      "The plan changed since you opened it. Refresh the project and review the dates again.",
    dependency_cycle:
      "Remove the circular dependency before applying these dates.",
  };
  return (
    messages[violation.code] ||
    "Resolve the schedule conflict before applying these dates."
  );
}

function applyTaskLevels(tasks: ProjectTask[]) {
  const byId = new Map(tasks.map((task) => [task.id, task]));
  const levelFor = (task: ProjectTask, visited = new Set<string>()): number => {
    if (!task.parentId || visited.has(task.id)) return 0;
    const parent = byId.get(task.parentId);
    if (!parent) return 0;
    visited.add(task.id);
    return Math.min(3, levelFor(parent, visited) + 1);
  };
  tasks.forEach((task) => {
    task.level = levelFor(task);
  });
  return tasks;
}

export const useWorkspaceStore = defineStore("workspace", () => {
  const user = ref<SessionUser | null>(null);
  const projects = ref<ProjectSummary[]>([]);
  const archivedProjects = ref<ProjectSummary[]>([]);
  const myWork = ref<MyWorkItem[]>([]);
  const organizations = ref<Organization[]>([]);
  const invitations = ref<ProjectInvitation[]>([]);
  const exports = ref<ExportJob[]>([]);
  const templates = ref<ProjectTemplate[]>([]);
  const projectBrief = ref<ProjectBrief | null>(null);
  const active = ref<ProjectBundle | null>(null);
  const projectAccess = ref<ProjectAccess | null>(null);
  const notifications = ref<NotificationItem[]>([]);
  const notificationPreferences = ref<NotificationPreferences>({
    immediate: false,
    digest: "daily",
  });
  const workspaceSettings = ref<WorkspaceSettings>({
    workspaceName: "VIPdesk",
    defaultTimezone: "America/Toronto",
    supportEmail: "support@vipdesk.com",
    emailSenderName: "VIPdesk Project Planner",
    emailReplyTo: "support@vipdesk.com",
    emailDeliveryMode: useEmulators ? "emulator" : "ses",
    digestHour: 8,
    invitationExpiryDays: 14,
    version: 1,
  });
  const emailTemplates = ref<EmailTemplatePreview[]>([]);
  const directorySyncStatus = ref<DirectorySyncStatus | null>(null);
  const loading = ref(false);
  const online = ref(true);
  const usingDemo = ref(useEmulators);
  const error = ref("");
  const announcement = ref("");
  const liveState = ref<RealtimeState>("connecting");
  const lastLiveAt = ref("");
  const workspaceLiveState = ref<RealtimeState>("connecting");
  const lastWorkspaceLiveAt = ref("");
  const reconcilingWorkspace = ref(false);
  const filters = ref({
    mine: false,
    organization: "All organizations",
    milestones: false,
    attention: false,
    hideInternal: false,
    statuses: [...DEFAULT_VISIBLE_STATUSES] as TaskStatus[],
    showNotApplicable: false,
    query: "",
  });

  const unreadCount = computed(
    () => notifications.value.filter((item) => !item.read).length,
  );
  const isInternal = computed(() => user.value?.kind === "internal");
  const canEditPlan = computed(
    () =>
      projectAccess.value?.canEditPlan === true ||
      (useEmulators && Boolean(user.value) && user.value?.role !== "Observer"),
  );
  const canManageProject = computed(
    () =>
      projectAccess.value?.canManageProject === true ||
      (useEmulators &&
        ["Project owner", "Workspace administrator"].includes(
          user.value?.role || "",
        )),
  );
  const canExportProject = computed(
    () =>
      projectAccess.value?.canExportProject === true ||
      (useEmulators &&
        ["Project owner", "Workspace administrator", "Client lead"].includes(
          user.value?.role || "",
        )),
  );
  let stopProjectRealtime: (() => void) | null = null;
  let stopNotificationRealtime: (() => void) | null = null;
  let stopWorkspaceRealtime: (() => void) | null = null;
  let projectRealtimeGeneration = 0;
  let notificationRealtimeGeneration = 0;
  let workspaceRealtimeGeneration = 0;
  let refreshInFlight: Promise<void> | null = null;
  let workspaceRefreshInFlight: Promise<void> | null = null;
  let workspaceRefreshPending = false;
  let accessRefreshInFlight: Promise<void> | null = null;
  let microsoftBootstrapGate: {
    promise: Promise<void>;
    resolve: () => void;
    successful: boolean;
  } | null = null;
  const dependencyIdByEdge = new Map<string, string>();
  const seenRoleChangeEvents = new Set<string>();
  const participantSignalRevisions = new Map<string, string>();

  function applyProjectAccess(value?: Record<string, unknown>) {
    if (!value) return;
    const role = String(value.role || "observer") as ProjectAccess["role"];
    projectAccess.value = {
      role,
      canViewInternal: value.canViewInternal === true,
      canEditPlan: value.canEditPlan === true,
      canManageProject: value.canManageProject === true,
      canComment: value.canComment === true,
      canExportProject: value.canExportProject === true,
    };
    if (user.value && user.value.role !== "Workspace administrator")
      user.value.role =
        role === "workspace_admin"
          ? "Workspace administrator"
          : projectRoleDefinition(role).label;
  }

  function setLiveState(state: RealtimeState) {
    liveState.value = state;
    if (state === "live") {
      online.value = true;
      lastLiveAt.value = new Date().toISOString();
    }
  }

  function stopRealtime() {
    projectRealtimeGeneration += 1;
    notificationRealtimeGeneration += 1;
    workspaceRealtimeGeneration += 1;
    stopProjectRealtime?.();
    stopNotificationRealtime?.();
    stopWorkspaceRealtime?.();
    stopProjectRealtime = null;
    stopNotificationRealtime = null;
    stopWorkspaceRealtime = null;
    liveState.value = "connecting";
    workspaceLiveState.value = "connecting";
  }

  function applyRealtimeCollection(
    projectId: string,
    name: ProjectionCollection,
    rows: Array<Record<string, unknown>>,
  ) {
    const current = active.value;
    if (!current || current.project.id !== projectId) return;

    if (name === "signals") {
      const revision = String(
        rows.find((row) => row.id === "participants")?.revision || "",
      );
      const previous = participantSignalRevisions.get(projectId);
      participantSignalRevisions.set(projectId, revision);
      if (revision && revision !== previous)
        void refreshProjectParticipants(projectId).catch(() => undefined);
      return;
    }

    if (name === "tasks") {
      const existing = new Map(current.tasks.map((task) => [task.id, task]));
      const next = [...rows]
        .sort((left, right) =>
          String(left.orderKey || "").localeCompare(
            String(right.orderKey || ""),
          ),
        )
        .map((row) => {
          const normalized = taskDto(row, current.participants);
          const found = existing.get(normalized.id);
          if (!found) return normalized;
          const comments = found.comments;
          const commentCount = found.commentCount;
          const fileCount = found.fileCount;
          const predecessorIds = found.predecessorIds;
          Object.assign(found, normalized, {
            comments,
            commentCount,
            fileCount,
            predecessorIds,
          });
          return found;
        });
      current.tasks = applyTaskLevels(next);
      return;
    }

    if (name === "sections") {
      current.sections = [...rows]
        .sort((left, right) =>
          String(left.orderKey || "").localeCompare(
            String(right.orderKey || ""),
          ),
        )
        .map((row) => sectionDto(row, current.tasks));
      return;
    }

    if (name === "dependencies") {
      const predecessors = new Map<string, string[]>();
      dependencyIdByEdge.clear();
      for (const row of rows) {
        const successorId = String(row.successorTaskId || "");
        const predecessorId = String(row.predecessorTaskId || "");
        if (!successorId || !predecessorId) continue;
        dependencyIdByEdge.set(
          `${predecessorId}>${successorId}`,
          String(row.id || ""),
        );
        predecessors.set(successorId, [
          ...(predecessors.get(successorId) || []),
          predecessorId,
        ]);
      }
      current.tasks.forEach((task) => {
        task.predecessorIds = predecessors.get(task.id) || [];
      });
      return;
    }

    if (name === "comments") {
      const grouped = new Map<string, TaskComment[]>();
      for (const row of [...rows].sort((left, right) =>
        String(left.createdAt || "").localeCompare(
          String(right.createdAt || ""),
        ),
      )) {
        const taskId = String(row.taskId || "");
        grouped.set(taskId, [
          ...(grouped.get(taskId) || []),
          commentDto(row, current.participants),
        ]);
      }
      current.tasks.forEach((task) => {
        task.comments = grouped.get(task.id) || [];
        task.commentCount = task.comments.filter(
          (comment) => !comment.deleted,
        ).length;
      });
      return;
    }

    if (name === "files") {
      current.files = rows.map((row) =>
        fileDto(row, current.tasks, current.participants),
      );
      current.tasks.forEach((task) => {
        task.fileCount = rows.filter((row) => row.parentId === task.id).length;
      });
      return;
    }

    if (name === "risks") {
      current.risks = rows.map((row) =>
        riskDto(row, current.tasks, current.participants),
      );
      return;
    }

    const unseenRoleChange = rows.some((row) => {
      if (row.type !== "participant.role_changed") return false;
      const eventId = String(row.id || "");
      if (!eventId || seenRoleChangeEvents.has(eventId)) return false;
      seenRoleChangeEvents.add(eventId);
      return true;
    });
    if (unseenRoleChange)
      void refreshProjectAccess(projectId).catch(() => undefined);

    current.activity = [...rows]
      .sort((left, right) =>
        String(right.occurredAt || "").localeCompare(
          String(left.occurredAt || ""),
        ),
      )
      .map((row) => activityDto(row, current.participants));
  }

  async function startProjectRealtime(projectId: string) {
    const generation = ++projectRealtimeGeneration;
    stopProjectRealtime?.();
    stopProjectRealtime = null;
    if (!auth.currentUser || !user.value) return;
    const { subscribeToProject } = await import("@/services/realtime");
    if (
      generation !== projectRealtimeGeneration ||
      !auth.currentUser ||
      !user.value ||
      active.value?.project.id !== projectId
    )
      return;
    stopProjectRealtime = subscribeToProject(
      projectId,
      user.value.kind === "internal" ? "internal" : "shared",
      {
        summary: (row) => {
          if (!row || active.value?.project.id !== projectId) return;
          const normalized = projectDto(row);
          normalized.clientOrganizationLogoDataUrl ||=
            active.value.project.clientOrganizationLogoDataUrl;
          active.value.project = normalized;
          const index = projects.value.findIndex(
            (item) => item.id === projectId,
          );
          if (index >= 0) projects.value[index] = normalized;
        },
        collection: (name, rows) =>
          applyRealtimeCollection(projectId, name, rows),
        state: setLiveState,
        error: (realtimeError) => {
          online.value = false;
          liveState.value = "paused";
          if (realtimeError.code === "permission-denied") {
            active.value = null;
            error.value =
              "Your access to this project changed. Return to Projects or ask the project owner to restore access.";
          }
        },
      },
    );
  }

  async function startNotificationRealtime() {
    const generation = ++notificationRealtimeGeneration;
    stopNotificationRealtime?.();
    stopNotificationRealtime = null;
    if (!auth.currentUser || !user.value) return;
    const { subscribeToNotifications } = await import("@/services/realtime");
    if (
      generation !== notificationRealtimeGeneration ||
      !auth.currentUser ||
      !user.value
    )
      return;
    stopNotificationRealtime = subscribeToNotifications(
      user.value.id,
      (rows) => {
        notifications.value = rows
          .sort((left, right) =>
            String(right.createdAt || "").localeCompare(
              String(left.createdAt || ""),
            ),
          )
          .map((row) => notificationDto(row, projects.value));
      },
      () => {
        liveState.value = "paused";
      },
    );
  }

  async function startWorkspaceRealtime() {
    const generation = ++workspaceRealtimeGeneration;
    stopWorkspaceRealtime?.();
    stopWorkspaceRealtime = null;
    if (!auth.currentUser || !user.value) return;
    const actorId = user.value.id;
    const { subscribeToWorkspace } = await import("@/services/realtime");
    if (
      generation !== workspaceRealtimeGeneration ||
      !auth.currentUser ||
      user.value?.id !== actorId
    )
      return;
    let lastServerRevision = "";
    stopWorkspaceRealtime = subscribeToWorkspace(actorId, {
      change: (revision, fromCache) => {
        if (fromCache || !revision || revision === lastServerRevision) return;
        lastServerRevision = revision;
        void reconcileWorkspaceFromApi();
      },
      state: (state) => {
        workspaceLiveState.value = state;
        if (state === "live") {
          online.value = true;
          lastWorkspaceLiveAt.value = new Date().toISOString();
        }
      },
      error: () => {
        online.value = false;
      },
    });
  }

  function setDemo(kind: "internal" | "admin" | "client") {
    if (!useEmulators)
      throw new Error(
        "Demo profiles are restricted to the local emulator environment.",
      );
    user.value = demoUsers[kind];
    if (kind === "client") {
      filters.value.organization = "All organizations";
    }
    projects.value = demoProjects;
    active.value = demoBundle(kind);
    projectAccess.value = {
      role:
        kind === "admin"
          ? "workspace_admin"
          : kind === "internal"
            ? "owner"
            : "client_lead",
      canViewInternal: kind !== "client",
      canEditPlan: true,
      canManageProject: kind !== "client",
      canComment: true,
      canExportProject: true,
    };
    notifications.value =
      kind !== "client"
        ? structuredClone(demoNotifications)
        : structuredClone(
            demoNotifications.filter((item) => item.kind !== "risk"),
          );
    usingDemo.value = true;
  }

  async function restoreSession() {
    loading.value = true;
    onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        stopRealtime();
        user.value = null;
        active.value = null;
        projectAccess.value = null;
        loading.value = false;
        return;
      }
      if (useEmulators && firebaseUser.email === "owner@vipdesk.local")
        setDemo("internal");
      else if (useEmulators && firebaseUser.email === "admin@vipdesk.local")
        setDemo("admin");
      else if (useEmulators && firebaseUser.email === "client@thorne.local")
        setDemo("client");
      const bootstrapGate = microsoftBootstrapGate;
      if (bootstrapGate) {
        await bootstrapGate.promise;
        if (!bootstrapGate.successful) {
          loading.value = false;
          return;
        }
      }
      const mfaStatus = await externalMfaSessionStatus();
      if (
        mfaStatus === "mfa-enrollment-required" ||
        mfaStatus === "mfa-sign-in-required"
      ) {
        user.value = null;
        loading.value = false;
        return;
      }
      await refreshFromApi();
      loading.value = false;
    });
  }

  async function refreshFromApi() {
    if (!auth.currentUser) return;
    if (refreshInFlight) return refreshInFlight;
    const refresh = (async () => {
      try {
        const session = await api.session();
        user.value = sessionDto(session);
        const projectList = await api.projects();
        if (Array.isArray(projectList))
          projects.value = projectList.map((item) =>
            projectDto(item as Record<string, unknown>),
          );
        await loadMyWork();
        const noticeList = await api.notifications();
        if (Array.isArray(noticeList))
          notifications.value = noticeList.map((item) =>
            notificationDto(item as Record<string, unknown>, projects.value),
          );
        startNotificationRealtime();
        startWorkspaceRealtime();
        usingDemo.value = useEmulators;
        online.value = true;
        error.value = "";
      } catch (refreshError) {
        if (!useEmulators) {
          if (refreshError instanceof ApiError && refreshError.status === 403) {
            const usesMicrosoft = auth.currentUser?.providerData.some(
              (provider) => provider.providerId === "microsoft.com",
            );
            error.value = usesMicrosoft
              ? "Your Microsoft account is signed in, but workspace eligibility needs to be verified again. Select Continue with Microsoft to continue."
              : "Your account is signed in, but its workspace access is not active. Ask a project owner or workspace administrator for help.";
            online.value = true;
          } else if (
            refreshError instanceof ApiError &&
            refreshError.status === 401
          ) {
            error.value = "Your session expired. Sign in again to continue.";
            online.value = true;
          } else {
            error.value =
              "We could not reach your workspace. Check your connection and try again.";
            online.value = false;
          }
        }
      }
    })();
    refreshInFlight = refresh;
    try {
      await refresh;
    } finally {
      if (refreshInFlight === refresh) refreshInFlight = null;
    }
  }

  async function reconcileWorkspaceFromApi() {
    if (!auth.currentUser || !user.value) return;
    if (workspaceRefreshInFlight) {
      workspaceRefreshPending = true;
      return workspaceRefreshInFlight;
    }
    const refresh = (async () => {
      do {
        workspaceRefreshPending = false;
        reconcilingWorkspace.value = true;
        const previousProjects = new Map(
          projects.value.map((project) => [project.id, project]),
        );
        try {
          const [projectList, workList] = await Promise.all([
            api.projects(),
            api.myWork(),
          ]);
          const nextProjects = Array.isArray(projectList)
            ? projectList.map((item) =>
                projectDto(item as Record<string, unknown>),
              )
            : [];
          projects.value = nextProjects;
          myWork.value = Array.isArray(workList) ? workList.map(myWorkDto) : [];
          const added = nextProjects.filter(
            (project) => !previousProjects.has(project.id),
          );
          const removed = [...previousProjects.values()].filter(
            (project) => !nextProjects.some((item) => item.id === project.id),
          );
          if (added.length === 1)
            announcement.value = `${added[0]!.name} is now available in Projects.`;
          else if (added.length > 1)
            announcement.value = `${added.length} projects are now available.`;
          else if (removed.length)
            announcement.value =
              "Your project access changed. Projects has been updated.";
          online.value = true;
          if (active.value)
            void refreshProjectAccess(active.value.project.id).catch(
              () => undefined,
            );
        } catch {
          workspaceLiveState.value = "paused";
          online.value = false;
        } finally {
          reconcilingWorkspace.value = false;
        }
      } while (
        workspaceRefreshPending &&
        Boolean(auth.currentUser) &&
        Boolean(user.value)
      );
    })();
    workspaceRefreshInFlight = refresh;
    try {
      await refresh;
    } finally {
      if (workspaceRefreshInFlight === refresh) workspaceRefreshInFlight = null;
    }
  }

  async function retryWorkspaceRealtime() {
    workspaceRealtimeGeneration += 1;
    stopWorkspaceRealtime?.();
    stopWorkspaceRealtime = null;
    workspaceLiveState.value = "connecting";
    await reconcileWorkspaceFromApi();
    await startWorkspaceRealtime();
  }

  async function loadMyWork() {
    if (!auth.currentUser) {
      myWork.value = [];
      return myWork.value;
    }
    const response = await api.myWork();
    myWork.value = Array.isArray(response) ? response.map(myWorkDto) : [];
    return myWork.value;
  }

  async function login(kind: "internal" | "admin" | "client" | "microsoft") {
    loading.value = true;
    error.value = "";
    let bootstrapGate: typeof microsoftBootstrapGate = null;
    if (kind === "microsoft") {
      let resolveBootstrap: () => void = () => undefined;
      const promise = new Promise<void>((resolve) => {
        resolveBootstrap = resolve;
      });
      bootstrapGate = {
        promise,
        resolve: resolveBootstrap,
        successful: false,
      };
      microsoftBootstrapGate = bootstrapGate;
    }
    try {
      if (kind === "microsoft") {
        const microsoftCredential = await signInMicrosoft();
        if (useEmulators) await api.bootstrapDevelopmentIdentity();
        else await api.bootstrapInternalIdentity(microsoftCredential);
        if (bootstrapGate) {
          bootstrapGate.successful = true;
          bootstrapGate.resolve();
        }
      } else {
        try {
          await signInDemo(kind);
        } catch {
          if (!useEmulators)
            throw new Error("Demo sign-in is only available locally.");
          setDemo(kind);
        }
      }
      if (kind !== "microsoft" && useEmulators) setDemo(kind);
      if (kind === "microsoft") await refreshFromApi();
    } catch (loginError) {
      bootstrapGate?.resolve();
      if (kind === "microsoft" && auth.currentUser) {
        await signOutUser().catch(() => undefined);
      }
      error.value =
        kind === "microsoft"
          ? microsoftWorkspaceErrorMessage(loginError)
          : "We could not open the demo profile. Make sure the Firebase Auth emulator is running, then try again.";
    } finally {
      bootstrapGate?.resolve();
      if (microsoftBootstrapGate === bootstrapGate)
        microsoftBootstrapGate = null;
      loading.value = false;
    }
  }

  async function loginExternal(email: string, password: string) {
    loading.value = true;
    error.value = "";
    user.value = null;
    try {
      const signIn = await signInExternal(email, password);
      if (signIn.status !== "signed-in") return signIn;
      await refreshFromApi();
      if (!user.value) {
        await signOutUser();
        throw new Error("workspace_access_unavailable");
      }
      return signIn;
    } catch {
      error.value =
        "We could not sign you in with that email and password. Check your details, or use the setup link in your invitation if this is your first visit.";
    } finally {
      loading.value = false;
    }
  }

  async function completeExternalMfa(
    challenge: ExternalMfaChallenge,
    factorUid: string,
    code: string,
  ) {
    loading.value = true;
    error.value = "";
    user.value = null;
    try {
      await completeExternalMfaSignIn(challenge, factorUid, code);
      await refreshFromApi();
      if (!user.value) {
        await signOutUser();
        throw new Error("workspace_access_unavailable");
      }
    } finally {
      loading.value = false;
    }
  }

  async function logout() {
    stopRealtime();
    try {
      await signOutUser();
    } catch {
      /* local fallback has no Firebase session */
    }
    user.value = null;
    active.value = null;
    projectAccess.value = null;
    projects.value = [];
    archivedProjects.value = [];
    myWork.value = [];
    organizations.value = [];
    invitations.value = [];
    exports.value = [];
    templates.value = [];
    projectBrief.value = null;
    notifications.value = [];
    usingDemo.value = useEmulators;
    filters.value = {
      mine: false,
      organization: "All organizations",
      milestones: false,
      attention: false,
      hideInternal: false,
      statuses: [...DEFAULT_VISIBLE_STATUSES],
      showNotApplicable: false,
      query: "",
    };
    sessionStorage.removeItem("planner-demo-profile");
  }

  async function loadProject(projectId: string) {
    projectBrief.value = null;
    projectAccess.value = null;
    seenRoleChangeEvents.clear();
    participantSignalRevisions.delete(projectId);
    projectRealtimeGeneration += 1;
    stopProjectRealtime?.();
    stopProjectRealtime = null;
    liveState.value = "connecting";
    loading.value = true;
    error.value = "";
    const kind = user.value?.kind || "internal";
    if (useEmulators) {
      if (projectId !== "prj_demo0001") {
        const summary = projects.value.find((item) => item.id === projectId);
        const bundle = demoBundle(kind);
        if (summary) bundle.project = summary;
        active.value = bundle;
      } else active.value = demoBundle(kind);
    } else active.value = null;
    if (auth.currentUser) {
      try {
        const [
          projectResponse,
          sectionRows,
          taskRows,
          dependencyRows,
          participantRows,
          fileRows,
          riskRows,
          activityRows,
        ] = await Promise.all([
          api.project(projectId),
          api.sections(projectId),
          api.tasks(projectId),
          api.dependencies(projectId),
          api.participants(projectId),
          api.files(projectId),
          api.risks(projectId),
          api.activity(projectId),
        ]);
        const response = projectResponse as {
          project: Record<string, unknown>;
          access?: Record<string, unknown>;
        };
        applyProjectAccess(response.access);
        const participants = Array.isArray(participantRows)
          ? participantRows.map((row) =>
              participantDto(row as Record<string, unknown>),
            )
          : [];
        const tasks = Array.isArray(taskRows)
          ? applyTaskLevels(
              taskRows.map((row) =>
                taskDto(row as Record<string, unknown>, participants),
              ),
            )
          : [];
        const sections = Array.isArray(sectionRows)
          ? sectionRows.map((row) =>
              sectionDto(row as Record<string, unknown>, tasks),
            )
          : [];
        const predecessors = new Map<string, string[]>();
        dependencyIdByEdge.clear();
        if (Array.isArray(dependencyRows))
          for (const rowValue of dependencyRows) {
            const row = rowValue as Record<string, unknown>;
            const predecessorId = String(row.predecessorTaskId || "");
            const successorId = String(row.successorTaskId || "");
            if (!predecessorId || !successorId) continue;
            predecessors.set(successorId, [
              ...(predecessors.get(successorId) || []),
              predecessorId,
            ]);
            dependencyIdByEdge.set(
              `${predecessorId}>${successorId}`,
              String(row.id || ""),
            );
          }
        tasks.forEach((task) => {
          task.predecessorIds = predecessors.get(task.id) || [];
        });
        const files = Array.isArray(fileRows)
          ? fileRows.map((row) =>
              fileDto(row as Record<string, unknown>, tasks, participants),
            )
          : [];
        const risks = Array.isArray(riskRows)
          ? riskRows.map((row) =>
              riskDto(row as Record<string, unknown>, tasks, participants),
            )
          : [];
        const activity = Array.isArray(activityRows)
          ? activityRows.map((row) =>
              activityDto(row as Record<string, unknown>, participants),
            )
          : [];
        active.value = {
          project: projectDto(response.project),
          sections,
          tasks,
          participants,
          files,
          risks,
          activity,
        };
        usingDemo.value = useEmulators;
      } catch {
        if (useEmulators) usingDemo.value = true;
        else {
          active.value = null;
          error.value =
            "We could not open this project. Your work is safe. Return to Projects and try again.";
        }
      }
    }
    if (auth.currentUser && active.value?.project.id === projectId)
      startProjectRealtime(projectId);
    loading.value = false;
  }

  async function refreshProjectAccess(projectId = active.value?.project.id) {
    if (
      !projectId ||
      !auth.currentUser ||
      active.value?.project.id !== projectId
    )
      return;
    if (accessRefreshInFlight) return accessRefreshInFlight;
    const refresh = (async () => {
      const previousAudience = projectAccess.value?.canViewInternal;
      try {
        const response = (await api.project(projectId)) as {
          project: Record<string, unknown>;
          access?: Record<string, unknown>;
        };
        if (active.value?.project.id !== projectId) return;
        applyProjectAccess(response.access);
        const normalized = projectDto(response.project);
        normalized.clientOrganizationLogoDataUrl ||=
          active.value.project.clientOrganizationLogoDataUrl;
        active.value.project = normalized;
        const nextAudience = projectAccess.value?.canViewInternal;
        if (
          previousAudience !== undefined &&
          nextAudience !== undefined &&
          previousAudience !== nextAudience
        )
          await loadProject(projectId);
      } catch (refreshError) {
        if (refreshError instanceof ApiError && refreshError.status === 403) {
          stopProjectRealtime?.();
          stopProjectRealtime = null;
          active.value = null;
          projectAccess.value = null;
          error.value =
            "Your access to this project changed. Return to Projects or ask a project owner to restore access.";
        }
        throw refreshError;
      }
    })();
    accessRefreshInFlight = refresh;
    try {
      await refresh;
    } finally {
      if (accessRefreshInFlight === refresh) accessRefreshInFlight = null;
    }
  }

  async function refreshProjectParticipants(
    projectId = active.value?.project.id,
  ) {
    if (
      !projectId ||
      !auth.currentUser ||
      active.value?.project.id !== projectId
    )
      return;
    const rows = await api.participants(projectId);
    if (active.value?.project.id !== projectId) return;
    active.value.participants = Array.isArray(rows)
      ? rows.map((row) => participantDto(row as Record<string, unknown>))
      : [];
  }

  async function searchWorkspace(query = ""): Promise<SearchResult[]> {
    if (!auth.currentUser) {
      const needle = query.trim().toLowerCase();
      return projects.value
        .filter((project) =>
          `${project.name} ${project.code} ${project.description}`
            .toLowerCase()
            .includes(needle),
        )
        .slice(0, 8)
        .map((project) => ({
          id: project.id,
          type: "project",
          title: project.name,
          context: project.code || "Project",
          projectId: project.id,
          route: `/projects/${project.id}/list`,
        }));
    }
    const response = (await api.search(query)) as {
      results?: SearchResult[];
    };
    return Array.isArray(response.results) ? response.results : [];
  }

  async function loadOrganizations() {
    if (!auth.currentUser) return organizations.value;
    const response = await api.organizations();
    organizations.value = Array.isArray(response)
      ? (response as Organization[]).map((organization) => ({
          ...organization,
          logoUrl: organization.logoUrl || "",
          version: Number(organization.version || 1),
        }))
      : [];
    return organizations.value;
  }

  async function createOrganization(input: {
    name: string;
    website: string;
    logoUrl: string;
    notes: string;
  }) {
    const created = (await api.createOrganization(input)) as Organization;
    organizations.value = [...organizations.value, created].sort(
      (left, right) => left.name.localeCompare(right.name),
    );
    announcement.value = `${created.name} created. Project access was not changed.`;
    return created;
  }

  async function updateOrganization(
    organizationId: string,
    input: {
      name: string;
      website: string;
      logoUrl: string;
      notes: string;
      version: number;
    },
  ) {
    const updated = (await api.updateOrganization(
      organizationId,
      input,
    )) as Organization;
    const index = organizations.value.findIndex(
      (organization) => organization.id === organizationId,
    );
    if (index >= 0) organizations.value[index] = updated;
    organizations.value.sort((left, right) =>
      left.name.localeCompare(right.name),
    );
    announcement.value = `${updated.name} updated. Project access was not changed.`;
    return updated;
  }

  async function directoryUsers(query = "", offset = 0) {
    const response = (await api.directoryUsers(
      query,
      offset,
      active.value?.project.id || "",
    )) as DirectoryPeoplePage;
    return response && Array.isArray(response.items)
      ? response
      : { items: [], total: 0, nextOffset: null };
  }

  async function loadInvitations(projectId = active.value?.project.id) {
    if (!projectId || !auth.currentUser) {
      invitations.value = [];
      return invitations.value;
    }
    const response = await api.invitations(projectId);
    invitations.value = Array.isArray(response)
      ? (response as ProjectInvitation[]).filter(
          (invitation) => invitation.state === "pending",
        )
      : [];
    return invitations.value;
  }

  async function inviteParticipant(input: {
    email: string;
    displayName: string;
    organizationId?: string;
    role: ProjectInvitation["role"];
  }) {
    if (!active.value)
      throw new Error("Open a project before inviting people.");
    const created = (await api.createInvitation(
      active.value.project.id,
      input,
    )) as ProjectInvitation;
    invitations.value.unshift(created);
    announcement.value = ["emulator_queued", "queued", "accepted"].includes(
      created.deliveryState,
    )
      ? `Account setup email queued for ${created.displayName}.`
      : `Invitation created for ${created.displayName}. Email delivery still needs configuration.`;
    return created;
  }

  async function resendInvitation(invitationId: string) {
    if (!active.value) return;
    const updated = (await api.resendInvitation(
      active.value.project.id,
      invitationId,
    )) as Pick<
      ProjectInvitation,
      "id" | "state" | "deliveryState" | "expiresAt"
    >;
    const invitation = invitations.value.find(
      (item) => item.id === invitationId,
    );
    if (invitation) Object.assign(invitation, updated);
    announcement.value = ["emulator_queued", "queued", "accepted"].includes(
      updated.deliveryState,
    )
      ? "A new invitation email is queued. The previous link no longer works."
      : "The invitation was renewed. Email delivery still needs configuration.";
    return updated;
  }

  async function revokeInvitation(invitationId: string) {
    if (!active.value) return;
    await api.revokeInvitation(active.value.project.id, invitationId);
    invitations.value = invitations.value.filter(
      (item) => item.id !== invitationId,
    );
    announcement.value = "Invitation revoked.";
  }

  async function addParticipant(
    person: DirectoryPerson,
    role: "internal_member" | "client_lead" | "client_member" | "observer",
  ) {
    if (!active.value) return;
    const response = await api.addParticipant(active.value.project.id, {
      ...(person.id.startsWith("dir_")
        ? { directoryProfileId: person.directoryProfileId || person.id }
        : { actorId: person.id }),
      role,
    });
    const participant = participantDto(response as Record<string, unknown>);
    if (!active.value.participants.some((item) => item.id === participant.id))
      active.value.participants.push(participant);
    announcement.value = `${participant.name} added to the project.`;
    return participant;
  }

  async function removeParticipant(actorId: string) {
    if (!active.value) return;
    const participant = active.value.participants.find(
      (person) => person.id === actorId,
    );
    await api.removeParticipant(active.value.project.id, actorId);
    active.value.participants = active.value.participants.filter(
      (person) => person.id !== actorId,
    );
    announcement.value = `${participant?.name || "Participant"} removed from the project.`;
  }

  async function updateParticipantRole(
    participant: Participant,
    role: ProjectRole,
  ) {
    if (!active.value)
      throw new Error("Open a project before changing a participant role.");
    const response = await api.updateParticipantRole(
      active.value.project.id,
      participant.id,
      { role, version: participant.version },
    );
    const updated = {
      ...participant,
      ...participantDto(response as Record<string, unknown>),
      email: participant.email,
      photoUrl: participant.photoUrl,
      organization: participant.organization,
    };
    const index = active.value.participants.findIndex(
      (person) => person.id === updated.id,
    );
    if (index >= 0) active.value.participants[index] = updated;
    if (updated.id === user.value?.id) await refreshProjectAccess();
    announcement.value = `${updated.name}'s project role changed to ${updated.role}.`;
    return updated;
  }

  async function updateProjectDetails(input: {
    name: string;
    description: string;
    descriptionRichText?: RichTextDocument;
    targetEndDate: string;
    targetPolicy: ProjectSummary["targetPolicy"];
    reason?: string;
  }) {
    if (!active.value) throw new Error("Open a project before saving details.");
    const current = active.value.project;
    const response = await api.updateProject(current.id, {
      version: current.version,
      name: input.name,
      description: input.description,
      ...(input.descriptionRichText
        ? { descriptionRichText: input.descriptionRichText }
        : {}),
      targetEndDate: input.targetEndDate,
      boundaryPolicy: input.targetPolicy === "Warn only" ? "warn" : "hard",
      ...(input.targetEndDate !== current.targetEndDate
        ? { reason: input.reason }
        : {}),
    });
    const normalized = projectDto(response as Record<string, unknown>);
    active.value.project = normalized;
    const index = projects.value.findIndex(
      (project) => project.id === current.id,
    );
    if (index >= 0) projects.value[index] = normalized;
    announcement.value = "Project details saved.";
    return normalized;
  }

  async function updateProjectOrganization(clientOrganizationId: string) {
    if (!active.value)
      throw new Error("Open a project before changing its organization.");
    const current = active.value.project;
    const response = await api.updateProject(current.id, {
      version: current.version,
      clientOrganizationId: clientOrganizationId || null,
    });
    const normalized = projectDto(response as Record<string, unknown>);
    active.value.project = normalized;
    const index = projects.value.findIndex(
      (project) => project.id === current.id,
    );
    if (index >= 0) projects.value[index] = normalized;
    announcement.value = clientOrganizationId
      ? "Client organization linked to this project."
      : "Client organization removed from this project.";
    return normalized;
  }

  async function previewProjectScheduleMove(input: {
    startDate: string;
    weekdaysOnly: boolean;
    reason: string;
  }): Promise<ProjectScheduleMovePreview> {
    if (!active.value)
      throw new Error("Open a project before moving its schedule.");
    const current = active.value.project;
    return (await api.previewProjectScheduleMove(current.id, {
      ...input,
      projectVersion: current.version,
      expectedScheduleVersion: current.scheduleVersion,
    })) as ProjectScheduleMovePreview;
  }

  async function applyProjectScheduleMove(preview: ProjectScheduleMovePreview) {
    if (!active.value)
      throw new Error("Open a project before applying a schedule move.");
    const response = (await api.applyProjectScheduleMove(
      active.value.project.id,
      {
        previewId: preview.previewId,
        expectedProjectVersion: preview.expectedProjectVersion,
        expectedScheduleVersion: preview.expectedScheduleVersion,
      },
    )) as {
      project: Record<string, unknown>;
      changes: Record<string, unknown>[];
    };
    const normalizedProject = projectDto(response.project);
    active.value.project = normalizedProject;
    const projectIndex = projects.value.findIndex(
      (project) => project.id === normalizedProject.id,
    );
    if (projectIndex >= 0) projects.value[projectIndex] = normalizedProject;
    for (const row of response.changes) {
      const normalizedTask = taskDto(row, active.value.participants);
      const taskIndex = active.value.tasks.findIndex(
        (task) => task.id === normalizedTask.id,
      );
      if (taskIndex < 0) continue;
      const existing = active.value.tasks[taskIndex]!;
      active.value.tasks[taskIndex] = {
        ...normalizedTask,
        predecessorIds: existing.predecessorIds,
        comments: existing.comments,
        commentCount: existing.commentCount,
        fileCount: existing.fileCount,
        level: existing.level,
      };
    }
    applyTaskLevels(active.value.tasks);
    announcement.value = `Project schedule moved. ${response.changes.length} work item${response.changes.length === 1 ? "" : "s"} updated.`;
    return normalizedProject;
  }

  async function closeProject(reason: string) {
    if (!active.value) return;
    const current = active.value.project;
    const response = await api.closeProject(current.id, {
      version: current.version,
      reason,
    });
    active.value.project = projectDto(response as Record<string, unknown>);
    announcement.value = `${current.name} closed.`;
  }

  async function archiveProject(reason: string) {
    if (!active.value) return;
    const current = active.value.project;
    const response = await api.archiveProject(current.id, {
      version: current.version,
      reason,
    });
    const archived = projectDto(response as Record<string, unknown>);
    active.value.project = archived;
    projects.value = projects.value.filter(
      (project) => project.id !== current.id,
    );
    archivedProjects.value.unshift(archived);
    announcement.value = `${current.name} archived.`;
    return archived;
  }

  async function loadArchivedProjects() {
    if (!auth.currentUser) return archivedProjects.value;
    const response = await api.archivedProjects();
    archivedProjects.value = Array.isArray(response)
      ? response.map((project) =>
          projectDto(project as Record<string, unknown>),
        )
      : [];
    return archivedProjects.value;
  }

  async function restoreArchivedProject(projectId: string) {
    const current = archivedProjects.value.find(
      (project) => project.id === projectId,
    );
    if (!current)
      throw new Error("This archived project is no longer available.");
    const response = await api.restoreArchivedProject(projectId, {
      version: current.version,
    });
    const restored = projectDto(response as Record<string, unknown>);
    archivedProjects.value = archivedProjects.value.filter(
      (project) => project.id !== projectId,
    );
    projects.value.unshift(restored);
    announcement.value = `${restored.name} restored to completed projects.`;
    return restored;
  }

  async function deleteArchivedProject(
    projectId: string,
    confirmation: string,
  ) {
    const current = archivedProjects.value.find(
      (project) => project.id === projectId,
    );
    if (!current)
      throw new Error("This archived project is no longer available.");
    await api.deleteArchivedProject(projectId, {
      version: current.version,
      confirmation,
    });
    archivedProjects.value = archivedProjects.value.filter(
      (project) => project.id !== projectId,
    );
    announcement.value = `${current.name} deleted.`;
  }

  async function saveOwnerUpdate(ownerUpdate: string) {
    if (!active.value) return;
    const current = active.value.project;
    const response = await api.updateProject(current.id, {
      version: current.version,
      ownerUpdate,
    });
    active.value.project = projectDto(response as Record<string, unknown>);
    announcement.value = "Current outlook published.";
    return active.value.project;
  }

  async function loadExports(projectId = active.value?.project.id) {
    if (!projectId || !auth.currentUser) return exports.value;
    const response = await api.exports(projectId);
    exports.value = Array.isArray(response) ? (response as ExportJob[]) : [];
    return exports.value;
  }

  async function createExport(input: {
    audience: "internal" | "shared";
    format: "doc" | "html" | "pdf" | "xls";
  }) {
    if (!active.value) return;
    const created = (await api.createExport(
      active.value.project.id,
      input,
    )) as ExportJob;
    exports.value.unshift(created);
    announcement.value = `${input.audience === "internal" ? "Internal " : ""}${input.format === "xls" ? "Excel plan" : "project report"} ready.`;
    return created;
  }

  async function downloadExport(jobId: string) {
    const result = await api.downloadExport(jobId);
    const url = URL.createObjectURL(result.blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = result.fileName;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
    announcement.value = `${result.fileName} downloaded.`;
    return result.fileName;
  }

  async function loadTemplates() {
    if (!auth.currentUser) return templates.value;
    const response = await api.templates();
    templates.value = Array.isArray(response)
      ? (response as ProjectTemplate[])
      : [];
    return templates.value;
  }

  async function loadProjectBrief(
    projectId = active.value?.project.id,
  ): Promise<ProjectBrief> {
    if (!projectId)
      throw new Error("Open a project before viewing its project brief.");
    if (!auth.currentUser) {
      const empty: ProjectBrief = {
        projectId,
        visibility: "Internal",
        content: emptyRichTextDocument(),
        version: 0,
        updatedAt: "",
        updatedBy: "",
      };
      projectBrief.value = empty;
      return empty;
    }
    const response = (await api.projectBrief(projectId)) as Record<
      string,
      unknown
    >;
    const brief: ProjectBrief = {
      projectId: String(response.projectId || projectId),
      visibility: "Internal",
      content:
        response.content && typeof response.content === "object"
          ? (response.content as RichTextDocument)
          : richTextFromLegacyProjectBrief(response.sections),
      version: Number(response.version || 0),
      updatedAt: String(response.updatedAt || ""),
      updatedBy: String(response.updatedByDisplayName || ""),
    };
    projectBrief.value = brief;
    return brief;
  }

  async function saveProjectBrief(input: ProjectBrief): Promise<ProjectBrief> {
    if (!active.value)
      throw new Error("Open a project before saving its project brief.");
    const response = (await api.updateProjectBrief(active.value.project.id, {
      version: input.version,
      content: input.content,
    })) as Record<string, unknown>;
    const saved: ProjectBrief = {
      projectId: String(response.projectId || active.value.project.id),
      visibility: "Internal",
      content:
        response.content && typeof response.content === "object"
          ? (response.content as RichTextDocument)
          : input.content,
      version: Number(response.version || input.version + 1),
      updatedAt: String(response.updatedAt || ""),
      updatedBy: String(
        response.updatedByDisplayName || user.value?.displayName || "",
      ),
    };
    projectBrief.value = saved;
    announcement.value = "Internal project brief saved.";
    return saved;
  }

  async function createTemplate(input: { name: string; description: string }) {
    if (!active.value) return;
    const created = (await api.createTemplate(
      active.value.project.id,
      input,
    )) as ProjectTemplate;
    templates.value.unshift(created);
    announcement.value = `${created.name} template created.`;
    return created;
  }

  async function createRisk(input: {
    kind: "risk" | "issue";
    title: string;
    impactNarrative: string;
    neededDecision: string;
    severity: "low" | "medium" | "high" | "critical";
    likelihood: "unlikely" | "possible" | "likely";
    impact: "low" | "medium" | "high";
    dueDate: string | null;
    visibility: Visibility;
  }) {
    if (!active.value || !user.value) return;
    const response = await api.createRisk(active.value.project.id, {
      ...input,
      status: "open",
      ownerActorId: user.value.id,
      visibility: fromVisibility(input.visibility),
    });
    const normalized = riskDto(
      response as Record<string, unknown>,
      active.value.tasks,
      active.value.participants,
    );
    active.value.risks.unshift(normalized);
    announcement.value = `${normalized.kind} added.`;
    return normalized;
  }

  async function createProject(
    input: Partial<ProjectSummary> & {
      timezone?: string;
      clientOrganizationId?: string;
      templateId?: string;
    },
  ) {
    const newProject: ProjectSummary = {
      id: `prj_${crypto.randomUUID().replaceAll("-", "").slice(0, 12)}`,
      code: `VIP-${String(projects.value.length + 1).padStart(4, "0")}`,
      name: input.name || "Untitled project",
      client: input.client || "No client organization",
      clientOrganizationId: input.clientOrganizationId,
      clientOrganizationLogoDataUrl: input.clientOrganizationId
        ? organizations.value.find(
            (organization) => organization.id === input.clientOrganizationId,
          )?.logoDataUrl
        : undefined,
      description: input.description || "",
      descriptionRichText: input.descriptionRichText,
      ownerUpdate: "No current outlook has been published for this project.",
      ownerUpdateBy: user.value?.displayName || "Project owner",
      ownerUpdateAt: "",
      startDate: input.startDate || new Date().toISOString().slice(0, 10),
      targetEndDate:
        input.targetEndDate ||
        addDays(new Date().toISOString().slice(0, 10), 90),
      workingWeekdays: input.workingWeekdays || [1, 2, 3, 4, 5],
      holidays: input.holidays || [],
      progress: 0,
      health: "On track",
      status: "Active",
      nextMilestone: "Add the first milestone",
      nextMilestoneDate: input.targetEndDate || "",
      blockers: 0,
      overdue: 0,
      updatedAt: "Just now",
      targetPolicy: "Hard boundary",
      defaultVisibility: "Shared",
      scheduleVersion: 1,
      version: 1,
    };
    try {
      if (auth.currentUser) {
        const created = await api.createProject({
          name: input.name,
          description: input.description || "",
          ...(input.descriptionRichText
            ? { descriptionRichText: input.descriptionRichText }
            : {}),
          startDate: input.startDate,
          targetEndDate: input.targetEndDate,
          timezone: input.timezone || "America/Toronto",
          calendarId: "standard-weekdays",
          workingWeekdays: input.workingWeekdays || [1, 2, 3, 4, 5],
          holidays: input.holidays || [],
          boundaryPolicy: "hard",
          ...(input.clientOrganizationId
            ? { clientOrganizationId: input.clientOrganizationId }
            : {}),
          ...(input.templateId ? { templateId: input.templateId } : {}),
        });
        const normalized = projectDto(created as Record<string, unknown>);
        projects.value.unshift(normalized);
        const template = input.templateId
          ? templates.value.find((item) => item.id === input.templateId)
          : null;
        announcement.value = template
          ? `${normalized.name} created from ${template.name}. Sections and tasks were copied without owners or delivery progress.`
          : `${normalized.name} created.`;
        return normalized;
      }
    } catch {
      if (!useEmulators) throw new Error("The project could not be created.");
    }
    projects.value.unshift(newProject);
    announcement.value = `${newProject.name} created.`;
    return newProject;
  }

  async function updateTask(
    taskId: string,
    patch: Partial<ProjectTask>,
    expectedVersion?: number,
  ): Promise<ProjectTask> {
    const task = active.value?.tasks.find((item) => item.id === taskId);
    if (!task) throw new Error("This task is no longer available.");
    const version = expectedVersion ?? task.version;
    if (auth.currentUser) {
      try {
        const response = await api.updateTask(
          task.projectId,
          task.id,
          taskPatchDto(patch, version),
        );
        const normalized = taskDto(
          response as Record<string, unknown>,
          active.value?.participants || [],
        );
        const comments = task.comments;
        const commentCount = task.commentCount;
        const fileCount = task.fileCount;
        const predecessorIds = task.predecessorIds;
        Object.assign(task, normalized, {
          comments,
          commentCount,
          fileCount,
          predecessorIds,
        });
        applyTaskLevels(active.value?.tasks || []);
        announcement.value = `Changes saved for ${task.title}.`;
        return task;
      } catch (updateError) {
        if (
          updateError instanceof ApiError &&
          (updateError.status === 409 || updateError.code === "conflict")
        )
          throw updateError;
        if (updateError instanceof ApiError)
          throw new Error(updateError.message);
        throw new Error(
          "We could not save your changes. Your draft is still open. Check your connection and try again.",
        );
      }
    }
    Object.assign(task, patch, { version: task.version + 1 });
    applyTaskLevels(active.value?.tasks || []);
    if (["checklist", "questionnaire"].includes(task.type)) {
      task.progress = calculatedWorkItemProgress(task);
      task.status = contentDrivenStatus(task);
    } else if (task.type === "milestone" && patch.status)
      task.progress = ["Complete", "Not applicable"].includes(patch.status)
        ? 100
        : 0;
    else if (["Complete", "Not applicable"].includes(String(patch.status)))
      task.progress = 100;
    announcement.value = `Changes saved for ${task.title}.`;
    return task;
  }

  async function createTask(input: {
    title: string;
    sectionId: string;
    visibility: Visibility;
    type?: "task" | "checklist" | "questionnaire" | "summary" | "milestone";
    parentId?: string;
    startDate?: string;
    dueDate?: string;
    duration?: number;
  }) {
    if (!active.value || !user.value) return null;
    const startDate = input.startDate || todayLocalDate();
    const duration = input.duration ?? 5;
    const dueDate =
      input.dueDate ||
      (input.type === "milestone"
        ? startDate
        : addDays(startDate, duration - 1));
    const task: ProjectTask = {
      id: `tsk_${crypto.randomUUID().replaceAll("-", "").slice(0, 12)}`,
      projectId: active.value.project.id,
      sectionId: input.sectionId,
      parentId: input.parentId,
      level: input.parentId ? 1 : 0,
      type: input.type || "task",
      title: input.title,
      description: "",
      notes: "",
      ownerId: input.type === "summary" ? undefined : user.value.id,
      ownerName:
        input.type === "summary"
          ? "Calculated from child tasks"
          : user.value.displayName,
      ownerInitials: input.type === "summary" ? "—" : user.value.initials,
      ownerOrganization:
        input.type === "summary" ? "" : user.value.organization,
      startDate: input.type === "summary" ? "" : startDate,
      dueDate: input.type === "summary" ? "" : dueDate,
      duration:
        input.type === "milestone" || input.type === "summary" ? 0 : duration,
      status: "Not started",
      health: "On track",
      visibility: input.visibility,
      progress: 0,
      predecessorIds: [],
      commentCount: 0,
      fileCount: 0,
      version: 1,
      checklist: [],
      questions: [],
      comments: [],
    };
    try {
      if (auth.currentUser) {
        const created = await api.createTask(active.value.project.id, {
          sectionId: input.sectionId,
          type: input.type || "task",
          ...(input.parentId ? { parentTaskId: input.parentId } : {}),
          title: input.title,
          description: "",
          notes: "",
          status: "not_started",
          ownerActorId: input.type === "summary" ? null : user.value.id,
          startDate: input.type === "summary" ? null : task.startDate,
          dueDate: input.type === "summary" ? null : task.dueDate,
          durationDays:
            input.type === "milestone" || input.type === "summary"
              ? 0
              : duration,
          progress: 0,
          visibility: fromVisibility(input.visibility),
          orderKey: `z${Date.now()}`,
          planningWeight: 1,
          collaboratorActorIds: [],
          tags: [],
          checklist: [],
          questions: [],
          blockerDescription: null,
          issueOwnerActorId: null,
          issueOwnerNeeded: false,
        });
        const normalized = taskDto(
          created as Record<string, unknown>,
          active.value.participants,
        );
        const existing = active.value.tasks.find(
          (item) => item.id === normalized.id,
        );
        if (existing) Object.assign(existing, normalized);
        else active.value.tasks.push(normalized);
        applyTaskLevels(active.value.tasks);
        announcement.value = `${normalized.title} added.`;
        return normalized;
      }
    } catch {
      usingDemo.value = true;
    }
    active.value.tasks.push(task);
    applyTaskLevels(active.value.tasks);
    announcement.value = `${task.title} added.`;
    return task;
  }

  async function createSection(input: {
    name: string;
    visibility: Visibility;
  }) {
    if (!active.value) return null;
    const fallback = {
      id: `sec_${crypto.randomUUID().replaceAll("-", "").slice(0, 12)}`,
      name: input.name,
      progress: 0,
      orderKey:
        input.name.trim().toLocaleLowerCase() === "unassigned"
          ? "zzzz_unassigned"
          : `z${Date.now()}`,
    };
    if (auth.currentUser) {
      const created = await api.createSection(active.value.project.id, {
        name: input.name,
        description: "",
        orderKey:
          input.name.trim().toLocaleLowerCase() === "unassigned"
            ? "zzzz_unassigned"
            : `z${Date.now()}`,
        visibility: fromVisibility(input.visibility),
      });
      const normalized = sectionDto(
        created as Record<string, unknown>,
        active.value.tasks,
      );
      if (
        !active.value.sections.some((section) => section.id === normalized.id)
      )
        active.value.sections.push(normalized);
      announcement.value = `${normalized.name} section added.`;
      return normalized;
    }
    active.value.sections.push(fallback);
    announcement.value = `${fallback.name} section added.`;
    return fallback;
  }

  async function previewTaskImport(
    rows: Array<{
      title: string;
      section: string;
      durationDays: number;
      startDate: string;
    }>,
    visibility: Visibility,
  ): Promise<TaskImportPreview> {
    if (!active.value)
      throw new Error("Open a project before importing tasks.");
    if (!auth.currentUser) {
      return {
        previewId: `imp_${crypto.randomUUID().replaceAll("-", "").slice(0, 12)}`,
        projectVersion: active.value.project.version,
        scheduleVersion: active.value.project.scheduleVersion,
        visibility,
        tasks: rows.map((row, index) => ({
          id: `tsk_${crypto.randomUUID().replaceAll("-", "").slice(0, 12)}`,
          title: row.title,
          sectionId: `preview_${index}`,
          sectionName: row.section || "Unassigned",
          startDate: row.startDate,
          dueDate: addProjectWorkingDays(
            row.startDate,
            row.durationDays - 1,
            active.value!.project.workingWeekdays,
            active.value!.project.holidays,
          ),
          durationDays: row.durationDays,
        })),
        newSections: [],
        expiresAt: new Date(Date.now() + 15 * 60_000).toISOString(),
      };
    }
    const response = (await api.previewTaskImport(active.value.project.id, {
      projectVersion: active.value.project.version,
      visibility: fromVisibility(visibility),
      rows,
    })) as Omit<TaskImportPreview, "visibility"> & {
      visibility: "internal" | "shared";
    };
    return {
      ...response,
      visibility: response.visibility === "internal" ? "Internal" : "Shared",
    };
  }

  async function applyTaskImport(preview: TaskImportPreview) {
    if (!active.value)
      throw new Error("Open a project before importing tasks.");
    if (!auth.currentUser) {
      const sectionByName = new Map(
        active.value.sections.map((section) => [
          section.name.toLocaleLowerCase(),
          section,
        ]),
      );
      for (const imported of preview.tasks) {
        const sectionName = imported.sectionName || "Unassigned";
        let section = sectionByName.get(sectionName.toLocaleLowerCase());
        if (!section) {
          const createdSection = await createSection({
            name: sectionName,
            visibility: preview.visibility,
          });
          if (!createdSection) continue;
          section = createdSection;
          sectionByName.set(sectionName.toLocaleLowerCase(), section);
        }
        const created = await createTask({
          title: imported.title,
          sectionId: section.id,
          visibility: preview.visibility,
          startDate: imported.startDate,
          duration: imported.durationDays,
        });
        if (created) {
          created.ownerId = undefined;
          created.ownerName = "Unassigned";
          created.ownerInitials = "—";
          created.ownerOrganization = "";
        }
      }
      active.value.sections.sort((left, right) => {
        const leftUnassigned =
          left.name.trim().toLocaleLowerCase() === "unassigned";
        const rightUnassigned =
          right.name.trim().toLocaleLowerCase() === "unassigned";
        if (leftUnassigned !== rightUnassigned) return leftUnassigned ? 1 : -1;
        return String(left.orderKey || "").localeCompare(
          String(right.orderKey || ""),
        );
      });
      announcement.value = `${preview.tasks.length} task${preview.tasks.length === 1 ? "" : "s"} imported.`;
      return;
    }
    const response = (await api.applyTaskImport(active.value.project.id, {
      previewId: preview.previewId,
      projectVersion: preview.projectVersion,
    })) as {
      tasks: Record<string, unknown>[];
      sections: Record<string, unknown>[];
      project: Record<string, unknown>;
    };
    const normalizedTasks = response.tasks.map((row) =>
      taskDto(row, active.value!.participants),
    );
    const knownTaskIds = new Set(active.value.tasks.map((task) => task.id));
    active.value.tasks.push(
      ...normalizedTasks.filter((task) => !knownTaskIds.has(task.id)),
    );
    for (const row of response.sections) {
      const section = sectionDto(row, active.value.tasks);
      if (!active.value.sections.some((item) => item.id === section.id))
        active.value.sections.push(section);
    }
    active.value.sections.sort((left, right) => {
      const leftUnassigned =
        left.name.trim().toLocaleLowerCase() === "unassigned";
      const rightUnassigned =
        right.name.trim().toLocaleLowerCase() === "unassigned";
      if (leftUnassigned !== rightUnassigned) return leftUnassigned ? 1 : -1;
      return String(left.orderKey || "").localeCompare(
        String(right.orderKey || ""),
      );
    });
    active.value.project = projectDto(response.project);
    applyTaskLevels(active.value.tasks);
    announcement.value = `${normalizedTasks.length} task${normalizedTasks.length === 1 ? "" : "s"} imported.`;
  }

  async function previewBulkTaskEdit(
    taskIds: string[],
    changes: BulkTaskEditChanges,
  ): Promise<BulkTaskEditPreview> {
    if (!active.value) throw new Error("Open a project before editing tasks.");
    const selected = taskIds.map((taskId) => {
      const task = active.value!.tasks.find((item) => item.id === taskId);
      if (!task)
        throw new Error("One of the selected tasks is no longer available.");
      return task;
    });
    if (!auth.currentUser)
      return (() => {
        const editable = selected.filter((task) => task.type !== "summary");
        if (!editable.length)
          throw new Error(
            "Task collections use calculated fields. Select at least one task or milestone to apply these changes.",
          );
        return {
          previewId: `blk_${crypto.randomUUID().replaceAll("-", "").slice(0, 12)}`,
          expectedScheduleVersion: active.value.project.scheduleVersion,
          selectedCount: editable.length,
          affectedCount: editable.length,
          selectedTasks: editable.map((task) => ({
            id: task.id,
            title: task.title,
          })),
          excludedTasks: selected
            .filter((task) => task.type === "summary")
            .map((task) => ({
              id: task.id,
              title: task.title,
              reason: "derived_task_collection" as const,
            })),
          scheduleImpacts: [],
          changedFields: Object.keys(changes),
          expiresAt: new Date(Date.now() + 15 * 60_000).toISOString(),
        };
      })();
    return (await api.previewBulkTaskEdit(active.value.project.id, {
      tasks: selected.map((task) => ({ id: task.id, version: task.version })),
      changes: {
        ...changes,
        ...(changes.status ? { status: fromStatus(changes.status) } : {}),
      },
    })) as BulkTaskEditPreview;
  }

  async function applyBulkTaskEdit(
    preview: BulkTaskEditPreview,
    changes?: BulkTaskEditChanges,
  ) {
    if (!active.value) throw new Error("Open a project before editing tasks.");
    if (!auth.currentUser) {
      for (const selected of preview.selectedTasks) {
        const task = active.value.tasks.find((item) => item.id === selected.id);
        if (!task || !changes) continue;
        if (changes.ownerActorId !== undefined) {
          const owner = active.value.participants.find(
            (participant) => participant.id === changes.ownerActorId,
          );
          task.ownerId = owner?.id;
          task.ownerName = owner?.name || "Unassigned";
          task.ownerInitials = owner?.initials || "—";
          task.ownerOrganization = owner?.organization || "";
        }
        if (changes.sectionId) task.sectionId = changes.sectionId;
        if (changes.startDate) task.startDate = changes.startDate;
        if (changes.status) task.status = changes.status;
        task.version += 1;
      }
      announcement.value = `${preview.selectedCount} task${preview.selectedCount === 1 ? "" : "s"} updated.`;
      return;
    }
    const response = (await api.applyBulkTaskEdit(active.value.project.id, {
      previewId: preview.previewId,
      expectedScheduleVersion: preview.expectedScheduleVersion,
    })) as {
      tasks: Record<string, unknown>[];
      project: Record<string, unknown>;
    };
    for (const row of response.tasks) {
      const normalized = taskDto(row, active.value.participants);
      const index = active.value.tasks.findIndex(
        (task) => task.id === normalized.id,
      );
      if (index < 0) continue;
      const existing = active.value.tasks[index]!;
      active.value.tasks[index] = {
        ...normalized,
        predecessorIds: existing.predecessorIds,
        comments: existing.comments,
        commentCount: existing.commentCount,
        fileCount: existing.fileCount,
        level: existing.level,
      };
    }
    active.value.project = projectDto(response.project);
    applyTaskLevels(active.value.tasks);
    announcement.value = `${preview.selectedCount} task${preview.selectedCount === 1 ? "" : "s"} updated.`;
  }

  async function deleteTasks(taskIds: string[], confirmation: string) {
    if (!active.value) throw new Error("Open a project before deleting tasks.");
    const selected = taskIds.map((taskId) => {
      const task = active.value!.tasks.find((item) => item.id === taskId);
      if (!task)
        throw new Error("One of the selected tasks is no longer available.");
      return task;
    });
    if (confirmation !== "delete")
      throw new Error("Type delete to confirm task deletion.");
    const blockers = taskDeletionBlockers(active.value.tasks, taskIds);
    if (blockers.length)
      throw new Error(
        `${taskDeletionBlockedMessage(blockers)} Remove the listed child and dependency relationships, then try again. Nothing was deleted.`,
      );

    let response: {
      deletedTaskIds: string[];
      project?: Record<string, unknown>;
    };
    const localDemo = !auth.currentUser;
    if (!localDemo)
      response = (await api.deleteTasks(active.value.project.id, {
        tasks: selected.map((task) => ({ id: task.id, version: task.version })),
        confirmation,
      })) as {
        deletedTaskIds: string[];
        project: Record<string, unknown>;
      };
    else response = { deletedTaskIds: selected.map((task) => task.id) };

    const deletedIds = new Set(response.deletedTaskIds);
    const deletedTitles = new Set(selected.map((task) => task.title));
    active.value.tasks = active.value.tasks
      .filter((task) => !deletedIds.has(task.id))
      .map((task) => ({
        ...task,
        predecessorIds: task.predecessorIds.filter(
          (predecessorId) => !deletedIds.has(predecessorId),
        ),
      }));
    active.value.files = active.value.files.filter(
      (file) =>
        !(
          file.parentType === "task" &&
          file.parentId &&
          deletedIds.has(file.parentId)
        ),
    );
    if (localDemo)
      active.value.risks = active.value.risks.map((risk) =>
        deletedTitles.has(risk.linkedTask)
          ? { ...risk, linkedTask: "Project" }
          : risk,
      );
    myWork.value = myWork.value.filter((item) => !deletedIds.has(item.id));
    notifications.value = notifications.value.filter(
      (notification) =>
        !notification.taskId || !deletedIds.has(notification.taskId),
    );
    if (response.project) active.value.project = projectDto(response.project);
    applyTaskLevels(active.value.tasks);
    announcement.value = `${deletedIds.size} work item${deletedIds.size === 1 ? "" : "s"} deleted.`;
    return response.deletedTaskIds;
  }

  async function addDependency(
    predecessorTaskId: string,
    successorTaskId: string,
  ) {
    if (!active.value) return;
    if (auth.currentUser) {
      const created = (await api.createDependency(active.value.project.id, {
        predecessorTaskId,
        successorTaskId,
      })) as { id?: string };
      if (created.id)
        dependencyIdByEdge.set(
          `${predecessorTaskId}>${successorTaskId}`,
          created.id,
        );
    }
    const successor = active.value.tasks.find(
      (task) => task.id === successorTaskId,
    );
    if (successor && !successor.predecessorIds.includes(predecessorTaskId))
      successor.predecessorIds.push(predecessorTaskId);
    announcement.value = "Dependency added.";
  }

  async function removeDependency(
    predecessorTaskId: string,
    successorTaskId: string,
  ) {
    if (!active.value) return;
    const edge = `${predecessorTaskId}>${successorTaskId}`;
    const dependencyId = dependencyIdByEdge.get(edge);
    if (auth.currentUser && dependencyId)
      await api.removeDependency(active.value.project.id, dependencyId);
    const successor = active.value.tasks.find(
      (task) => task.id === successorTaskId,
    );
    if (successor)
      successor.predecessorIds = successor.predecessorIds.filter(
        (id) => id !== predecessorTaskId,
      );
    dependencyIdByEdge.delete(edge);
    announcement.value = "Dependency removed.";
  }

  async function setTaskStatus(taskId: string, status: TaskStatus) {
    error.value = "";
    try {
      return await updateTask(taskId, { status });
    } catch (statusError) {
      error.value =
        statusError instanceof Error
          ? statusError.message
          : "The task status could not be changed. Try again.";
      return null;
    }
  }

  async function addComment(
    taskId: string,
    body: string | RichTextDocument,
    visibility: Visibility,
    mentionActorIds: string[] = [],
    commentId?: string,
  ) {
    const task = active.value?.tasks.find((item) => item.id === taskId);
    if (!task || !user.value)
      throw new Error("This task is no longer available.");
    let comment: TaskComment;
    const bodyRichText = typeof body === "string" ? undefined : body;
    const plainBody = typeof body === "string" ? body : richTextPlainText(body);
    const resolvedMentionIds = bodyRichText
      ? richTextMentionIds(bodyRichText)
      : mentionActorIds;
    if (auth.currentUser) {
      try {
        const created = await api.comment(task.projectId, task.id, {
          ...(commentId ? { id: commentId } : {}),
          body: plainBody,
          ...(bodyRichText ? { bodyRichText } : {}),
          visibility: fromVisibility(visibility),
          mentionActorIds: resolvedMentionIds,
        });
        comment = commentDto(
          created as Record<string, unknown>,
          active.value?.participants || [],
        );
      } catch (commentError) {
        if (commentError instanceof ApiError)
          throw new Error(commentError.message);
        throw new Error(
          "We could not add your comment. Check your connection and try again.",
        );
      }
    } else {
      comment = {
        id: crypto.randomUUID(),
        author: user.value.displayName,
        initials: user.value.initials,
        photoUrl: user.value.photoUrl,
        createdAt: "Just now",
        body: plainBody,
        bodyRichText,
        visibility,
      };
    }
    if (!task.comments.some((entry) => entry.id === comment.id))
      task.comments.push(comment);
    task.commentCount = task.comments.length;
    announcement.value = `Comment added to ${task.title}.`;
    return comment;
  }

  async function uploadPastedImage(input: {
    projectId: string;
    parentType: "project" | "task" | "comment";
    parentId?: string;
    visibility: Visibility;
    file: File;
  }) {
    const extension = input.file.type === "image/jpeg" ? "jpg" : "png";
    const displayName =
      input.file.name && input.file.name !== "image.png"
        ? input.file.name
        : `Pasted image ${new Intl.DateTimeFormat("en-CA", {
            dateStyle: "medium",
            timeStyle: "short",
          }).format(new Date())}.${extension}`;
    const uploaded = await uploadFile({ ...input, displayName });
    announcement.value = "Image uploaded and attached.";
    return { fileId: uploaded.id, alt: displayName };
  }

  async function uploadFile(input: {
    projectId: string;
    parentType: "project" | "task" | "comment";
    parentId?: string;
    visibility: Visibility;
    file: File;
    displayName?: string;
  }) {
    if (!auth.currentUser)
      throw new Error(
        "Secure file storage is unavailable in this session. Sign in and try again.",
      );
    let pendingUpload: { id: string; version: number } | null = null;
    try {
      const created = (await api.createFile(input.projectId, {
        parentType: input.parentType,
        parentId: input.parentId || null,
        displayName: input.displayName || input.file.name,
        contentType: input.file.type,
        sizeBytes: input.file.size,
        visibility: fromVisibility(input.visibility),
      })) as Record<string, unknown>;
      const fileId = String(created.id || "");
      const uploadPath = String(created.uploadPath || "");
      if (!fileId || !uploadPath)
        throw new Error("The file upload could not be prepared.");
      pendingUpload = { id: fileId, version: Number(created.version || 1) };
      await uploadBytes(storageObjectRef(storage, uploadPath), input.file, {
        contentType: input.file.type,
        customMetadata: { fileId },
      });
      const completed = (await api.completeFileUpload(input.projectId, fileId, {
        version: Number(created.version || 1),
      })) as Record<string, unknown>;
      if (active.value?.project.id === input.projectId) {
        const normalized = fileDto(
          completed,
          active.value.tasks,
          active.value.participants,
        );
        const existing = active.value.files.findIndex(
          (file) => file.id === normalized.id,
        );
        if (existing >= 0) active.value.files[existing] = normalized;
        else active.value.files.unshift(normalized);
        if (input.parentType === "task" && input.parentId) {
          const task = active.value.tasks.find(
            (item) => item.id === input.parentId,
          );
          if (task)
            task.fileCount = active.value.files.filter(
              (file) => file.parentType === "task" && file.parentId === task.id,
            ).length;
        }
      }
      announcement.value = `${input.displayName || input.file.name} uploaded.`;
      return { id: fileId, record: completed };
    } catch (uploadError) {
      if (pendingUpload) {
        await api
          .abortFileUpload(input.projectId, pendingUpload.id, {
            version: pendingUpload.version,
          })
          .catch(() => undefined);
        if (active.value?.project.id === input.projectId) {
          active.value.files = active.value.files.filter(
            (file) => file.id !== pendingUpload?.id,
          );
        }
      }
      if (uploadError instanceof ApiError) throw new Error(uploadError.message);
      throw new Error(
        fileUploadErrorMessage(uploadError, { localDemo: useEmulators }),
      );
    }
  }

  async function deleteFile(projectId: string, fileId: string) {
    const current = active.value;
    const file = current?.files.find((entry) => entry.id === fileId);
    if (!current || current.project.id !== projectId || !file)
      throw new Error("This file is no longer available.");
    if (auth.currentUser) {
      try {
        await api.deleteFile(projectId, fileId, {
          version: file.version || 1,
          confirmation: "delete",
        });
      } catch (deleteError) {
        if (deleteError instanceof ApiError)
          throw new Error(deleteError.message);
        throw new Error(
          "We could not delete the file. Check your connection and try again.",
        );
      }
    }
    current.files = current.files.filter((entry) => entry.id !== fileId);
    current.tasks.forEach((task) => {
      task.fileCount = current.files.filter(
        (entry) => entry.parentType === "task" && entry.parentId === task.id,
      ).length;
    });
    announcement.value = `${file.name} deleted.`;
  }

  async function deleteComment(
    projectId: string,
    taskId: string,
    commentId: string,
  ) {
    const current = active.value;
    const task = current?.tasks.find((entry) => entry.id === taskId);
    const existing = task?.comments.find((entry) => entry.id === commentId);
    if (!current || current.project.id !== projectId || !task || !existing)
      throw new Error("This comment is no longer available.");
    let tombstone: TaskComment | null = null;
    if (auth.currentUser) {
      try {
        const result = (await api.deleteComment(projectId, taskId, commentId, {
          version: existing.version || 1,
          confirmation: "delete",
        })) as Record<string, unknown>;
        if (result.tombstone && typeof result.tombstone === "object")
          tombstone = commentDto(
            result.tombstone as Record<string, unknown>,
            current.participants,
          );
      } catch (deleteError) {
        if (deleteError instanceof ApiError)
          throw new Error(deleteError.message);
        throw new Error(
          "We could not delete the comment. Check your connection and try again.",
        );
      }
    }
    task.comments = tombstone
      ? task.comments.map((entry) =>
          entry.id === commentId ? tombstone! : entry,
        )
      : task.comments.filter((entry) => entry.id !== commentId);
    task.commentCount = task.comments.filter((entry) => !entry.deleted).length;
    current.files = current.files.filter(
      (file) => !(file.parentType === "comment" && file.parentId === commentId),
    );
    announcement.value = `Comment deleted from ${task.title}.`;
  }

  async function downloadFile(projectId: string, fileId: string) {
    const result = await api.downloadFile(projectId, fileId);
    const url = URL.createObjectURL(result.blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = result.fileName;
    anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  async function previewSchedule(
    taskId: string,
    newStart: string,
    newDue: string,
  ): Promise<ScheduleImpact | null> {
    const current = active.value;
    const task = current?.tasks.find((item) => item.id === taskId);
    if (!current || !task) return null;
    const shift = workingDaysBetween(task.dueDate, newDue);
    const affected = current.tasks
      .filter((item) => item.predecessorIds.includes(task.id))
      .map((item) => ({
        taskId: item.id,
        title: item.title,
        oldDue: item.dueDate,
        newDue: addWorkingDays(item.dueDate, shift),
        owner: item.ownerName,
      }));
    const latestProposedDate =
      [newDue, ...affected.map((item) => item.newDue)].sort().at(-1) || newDue;
    const crossesBoundary = latestProposedDate > current.project.targetEndDate;
    const localImpact: ScheduleImpact = {
      taskId,
      taskTitle: task.title,
      oldStart: task.startDate,
      oldDue: task.dueDate,
      newStart,
      newDue,
      affected,
      crossesBoundary,
      targetEndDate: current.project.targetEndDate,
      canApply: true,
      violationMessages: [],
      ...(crossesBoundary ? { requiredTargetEndDate: latestProposedDate } : {}),
    };
    if (auth.currentUser) {
      try {
        const response = (await api.previewSchedule(
          current.project.id,
          task.id,
          { startDate: newStart, dueDate: newDue, version: task.version },
        )) as {
          previewId: string;
          expectedScheduleVersion: number;
          changes: Array<{
            taskId: string;
            oldStartDate: string;
            oldDueDate: string;
            startDate: string;
            dueDate: string;
          }>;
          violations?: Array<{ code: string }>;
          boundaryConflict?: {
            requiresProjectEndExtension?: boolean;
            latestProposedEndDate?: string;
          } | null;
          canApply: boolean;
        };
        const root = response.changes.find(
          (change) => change.taskId === task.id,
        );
        const violations = response.violations || [];
        const crossesBoundary =
          response.boundaryConflict?.requiresProjectEndExtension === true;
        const hasNonBoundaryViolation = violations.some(
          (violation) =>
            violation.code !== "project_boundary_extension_required",
        );
        return {
          ...localImpact,
          ...(root ? { newStart: root.startDate, newDue: root.dueDate } : {}),
          affected: response.changes
            .filter((change) => change.taskId !== task.id)
            .map((change) => ({
              taskId: change.taskId,
              title:
                current.tasks.find((item) => item.id === change.taskId)
                  ?.title || "Dependent task",
              oldDue: change.oldDueDate,
              newDue: change.dueDate,
              owner:
                current.tasks.find((item) => item.id === change.taskId)
                  ?.ownerName || "Project participant",
            })),
          crossesBoundary,
          previewId: response.previewId,
          expectedScheduleVersion: response.expectedScheduleVersion,
          canApply:
            response.canApply || (crossesBoundary && !hasNonBoundaryViolation),
          violationMessages: violations.map(violationMessage),
          requiredTargetEndDate:
            response.boundaryConflict?.latestProposedEndDate ||
            localImpact.requiredTargetEndDate,
        };
      } catch {
        return {
          ...localImpact,
          canApply: false,
          violationMessages: [
            "We could not validate these dates. Keep the current dates and try again.",
          ],
        };
      }
    }
    return localImpact;
  }

  async function applySchedule(
    impact: ScheduleImpact,
    options?: { extensionReason?: string },
  ) {
    if (
      auth.currentUser &&
      impact.previewId &&
      impact.expectedScheduleVersion
    ) {
      await api.applySchedule(active.value!.project.id, {
        previewId: impact.previewId,
        expectedScheduleVersion: impact.expectedScheduleVersion,
        ...(impact.crossesBoundary && impact.requiredTargetEndDate
          ? {
              extendTargetEnd: impact.requiredTargetEndDate,
              extensionReason: options?.extensionReason?.trim(),
            }
          : {}),
      });
    }
    const root = active.value?.tasks.find((item) => item.id === impact.taskId);
    if (root)
      Object.assign(root, {
        startDate: impact.newStart,
        dueDate: impact.newDue,
        version: root.version + 1,
      });
    impact.affected.forEach((change) => {
      const item = active.value?.tasks.find(
        (task) => task.id === change.taskId,
      );
      if (item)
        Object.assign(item, {
          dueDate: change.newDue,
          version: item.version + 1,
        });
    });
    if (impact.crossesBoundary && impact.requiredTargetEndDate && active.value)
      active.value.project.targetEndDate = impact.requiredTargetEndDate;
    announcement.value = `Date changes applied to ${impact.taskTitle} and ${impact.affected.length} dependent ${impact.affected.length === 1 ? "task" : "tasks"}.`;
  }

  function markNotification(id: string) {
    const item = notifications.value.find((notice) => notice.id === id);
    if (item) item.read = true;
    if (auth.currentUser)
      api.markNotification(id).catch(() => {
        usingDemo.value = true;
      });
  }

  async function loadNotificationPreferences() {
    if (!auth.currentUser) return { ...notificationPreferences.value };
    const response = (await api.notificationPreferences()) as Record<
      string,
      unknown
    >;
    notificationPreferences.value = {
      immediate: response.immediate === true,
      digest:
        response.digest === "weekly" || response.digest === "off"
          ? response.digest
          : "daily",
    };
    return { ...notificationPreferences.value };
  }

  async function saveNotificationPreferences(input: NotificationPreferences) {
    if (auth.currentUser) {
      const response = (await api.updateNotificationPreferences(
        input,
      )) as Record<string, unknown>;
      notificationPreferences.value = {
        immediate: response.immediate === true,
        digest:
          response.digest === "weekly" || response.digest === "off"
            ? response.digest
            : "daily",
      };
    } else notificationPreferences.value = { ...input };
    return { ...notificationPreferences.value };
  }

  async function loadWorkspaceSettings() {
    if (!auth.currentUser) return { ...workspaceSettings.value };
    const response = (await api.workspaceSettings()) as WorkspaceSettings;
    workspaceSettings.value = { ...response };
    return { ...workspaceSettings.value };
  }

  async function loadDirectorySyncStatus() {
    if (!auth.currentUser) return directorySyncStatus.value;
    directorySyncStatus.value =
      (await api.directorySyncStatus()) as DirectorySyncStatus;
    return directorySyncStatus.value;
  }

  async function runDirectorySync() {
    if (!auth.currentUser)
      throw new Error(
        "Sign in as a workspace administrator to run a directory sync.",
      );
    const job = (await api.runDirectorySync()) as {
      id: string;
      state: string;
      createdAt: string;
    };
    if (directorySyncStatus.value)
      directorySyncStatus.value = {
        ...directorySyncStatus.value,
        state: "queued",
        queuedJobId: job.id,
      };
    announcement.value = "Corporate Team directory sync queued.";
    return job;
  }

  async function loadEmailTemplates() {
    if (!auth.currentUser) return emailTemplates.value;
    const response = await api.emailTemplates();
    emailTemplates.value = Array.isArray(response)
      ? (response as EmailTemplatePreview[])
      : [];
    return emailTemplates.value;
  }

  async function sendTestEmail(email: string) {
    if (!auth.currentUser)
      throw new Error(
        "Sign in as a workspace administrator to send a test email.",
      );
    return api.sendTestEmail(email) as Promise<{
      id: string;
      state: string;
    }>;
  }

  async function testEmailStatus(deliveryId: string) {
    return api.testEmailStatus(deliveryId) as Promise<{
      id: string;
      state: string;
      updatedAt: string;
    }>;
  }

  async function saveWorkspaceSettings(input: WorkspaceSettings) {
    if (auth.currentUser) {
      const response = (await api.updateWorkspaceSettings(
        input,
      )) as WorkspaceSettings;
      workspaceSettings.value = { ...response };
    } else
      workspaceSettings.value = {
        ...input,
        version: input.version + 1,
      };
    announcement.value = "Administration settings saved.";
    return { ...workspaceSettings.value };
  }

  return {
    user,
    projects,
    archivedProjects,
    myWork,
    organizations,
    invitations,
    exports,
    templates,
    projectBrief,
    active,
    projectAccess,
    notifications,
    notificationPreferences,
    workspaceSettings,
    emailTemplates,
    directorySyncStatus,
    loading,
    online,
    usingDemo,
    error,
    announcement,
    liveState,
    lastLiveAt,
    workspaceLiveState,
    lastWorkspaceLiveAt,
    reconcilingWorkspace,
    filters,
    unreadCount,
    isInternal,
    canEditPlan,
    canManageProject,
    canExportProject,
    restoreSession,
    refreshFromApi,
    reconcileWorkspaceFromApi,
    retryWorkspaceRealtime,
    login,
    loginExternal,
    completeExternalMfa,
    logout,
    loadProject,
    refreshProjectAccess,
    refreshProjectParticipants,
    loadMyWork,
    searchWorkspace,
    loadOrganizations,
    createOrganization,
    updateOrganization,
    directoryUsers,
    loadInvitations,
    inviteParticipant,
    resendInvitation,
    revokeInvitation,
    addParticipant,
    updateParticipantRole,
    removeParticipant,
    updateProjectDetails,
    updateProjectOrganization,
    previewProjectScheduleMove,
    applyProjectScheduleMove,
    closeProject,
    archiveProject,
    loadArchivedProjects,
    restoreArchivedProject,
    deleteArchivedProject,
    saveOwnerUpdate,
    loadExports,
    createExport,
    downloadExport,
    loadTemplates,
    createTemplate,
    loadProjectBrief,
    saveProjectBrief,
    createRisk,
    createProject,
    createTask,
    createSection,
    previewTaskImport,
    applyTaskImport,
    previewBulkTaskEdit,
    applyBulkTaskEdit,
    deleteTasks,
    updateTask,
    setTaskStatus,
    addDependency,
    removeDependency,
    addComment,
    uploadFile,
    deleteFile,
    deleteComment,
    downloadFile,
    uploadPastedImage,
    previewSchedule,
    applySchedule,
    markNotification,
    loadNotificationPreferences,
    saveNotificationPreferences,
    loadWorkspaceSettings,
    loadDirectorySyncStatus,
    runDirectorySync,
    loadEmailTemplates,
    sendTestEmail,
    testEmailStatus,
    saveWorkspaceSettings,
  };
});
